import { useEffect, useState } from "react";

/** Inline preview of an AI-generated element, for the preview box. */
function PreviewLine({ type, text }) {
  if (type === "scene_heading") return <p className="mt-2 font-bold uppercase text-stone-900">{text}</p>;
  if (type === "character") return <p className="mt-2 pl-12 font-bold uppercase">{text}</p>;
  if (type === "parenthetical") return <p className="pl-10 italic text-stone-600">({(text || "").replace(/^[()]+|[()]+$/g, "")})</p>;
  if (type === "dialogue") return <p className="pl-8 max-w-[80%]">{text}</p>;
  if (type === "transition") return <p className="mt-2 text-right font-bold uppercase">{text}</p>;
  if (type === "shot") return <p className="mt-2 font-bold uppercase text-[#26775E]">{text}</p>;
  if (type === "note") return <p className="italic text-amber-700">/* {text} */</p>;
  return <p className="text-stone-800">{text}</p>;
}

const DIRECTION_EMOJIS = ["🟢", "🔥", "💔"];

/** Formatea segundos como "12 s" o "1 m 23 s". */
function formatElapsed(secs) {
  const s = Math.max(0, Math.floor(Number(secs) || 0));
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)} m ${String(s % 60).padStart(2, "0")} s`;
}

/**
 * Right-side AI assistant panel with 3 modes (sequence / dialogue / rewrite),
 * cursor-context detector, and a "Suggest 3 directions" feature.
 *
 * All state about the screenplay lives in the parent; this panel only owns its
 * local form state (textareas + picked chars).
 */
export default function AssistantPanel({
  aiBusy,
  aiError,
  aiPreview,
  aiContext,
  aiElapsed,
  selectedCount,
  charNames,
  cursorContext,
  directions,
  initialMode,
  initialModeNonce,
  onGenerateSequence,
  onGenerateDialogue,
  onRewriteSelection,
  onSuggestDirections,
  onApplyPreview,
  onDismissPreview,
  onDismissDirections,
  onRetry,
  canRetry,
  onCancel,
}) {
  const [mode, setMode] = useState(initialMode || "sequence");
  const [instructions, setInstructions] = useState("");
  const [dlgContext, setDlgContext] = useState("");
  const [dlgExtra, setDlgExtra] = useState("");
  const [pickedChars, setPickedChars] = useState([]);
  const [useCursorContext, setUseCursorContext] = useState(true);
  const [directionsHint, setDirectionsHint] = useState("");

  // Sincroniza el modo cuando el padre lo pide (por ejemplo al pulsar
  // "Reescribir con IA" desde la barra de selección del editor). Usamos un
  // nonce para que el mismo valor pueda dispararlo varias veces seguidas.
  useEffect(() => {
    if (initialMode) setMode(initialMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialModeNonce]);

  const toggleChar = (name) => {
    setPickedChars((p) => (p.includes(name) ? p.filter((n) => n !== name) : [...p, name]));
  };

  const submit = () => {
    if (aiBusy) return;
    if (mode === "sequence") onGenerateSequence(instructions, pickedChars, useCursorContext);
    if (mode === "dialogue") onGenerateDialogue(dlgContext, pickedChars, dlgExtra);
    if (mode === "rewrite") onRewriteSelection(instructions);
  };

  const pickDirection = (instruction) => {
    setInstructions(instruction);
    onDismissDirections?.();
  };

  const hasCtx = !!(cursorContext && (cursorContext.heading || cursorContext.lastAction));

  return (
    <div data-testid="sp-assistant-panel">
      <h4 className="mb-3 font-serif text-base text-stone-900">Asistente IA</h4>

      {/* Mode tabs */}
      <div className="mb-3 grid grid-cols-3 gap-1 rounded-lg bg-stone-100 p-1">
        {[
          { id: "sequence", label: "Secuencia" },
          { id: "dialogue", label: "Diálogo" },
          { id: "rewrite", label: "Reescribir" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setMode(t.id)}
            data-testid={`sp-ai-mode-${t.id}`}
            className={`rounded-md px-2 py-1.5 text-[11px] uppercase tracking-widest transition ${
              mode === t.id ? "bg-[#26775E] text-white" : "text-stone-700 hover:bg-stone-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Cursor context detector (only in sequence mode) */}
      {mode === "sequence" && hasCtx && (
        <div className="mb-3 rounded-lg border border-[#26775E]/30 bg-[#26775E]/5 p-3" data-testid="sp-ai-cursor-context">
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#26775E]">📍 Contexto detectado</span>
            <label className="flex shrink-0 items-center gap-1 text-[10px] uppercase tracking-widest text-stone-600" title="Si lo desmarcas, la IA recibirá solo tu instrucción literal.">
              <input
                type="checkbox"
                checked={useCursorContext}
                onChange={(e) => setUseCursorContext(e.target.checked)}
                data-testid="sp-ai-use-context"
                className="h-3.5 w-3.5 accent-[#26775E]"
              />
              Usar
            </label>
          </div>
          {cursorContext.heading && (
            <p className="text-[11px] text-stone-700">
              <span className="text-stone-500">Escena:</span>{" "}
              <span className="font-mono font-bold uppercase">
                {cursorContext.sceneNumber ? `#${cursorContext.sceneNumber} · ` : ""}
                {cursorContext.heading}
              </span>
            </p>
          )}
          {cursorContext.characters?.length > 0 && (
            <p className="mt-0.5 text-[11px] text-stone-700">
              <span className="text-stone-500">Personajes presentes:</span>{" "}
              <span className="font-mono uppercase">{cursorContext.characters.join(" · ")}</span>
            </p>
          )}
          {cursorContext.lastAction && (
            <p className="mt-0.5 truncate text-[11px] italic text-stone-600" title={cursorContext.lastAction}>
              <span className="text-stone-500 not-italic">Última acción:</span> &ldquo;{cursorContext.lastAction}&rdquo;
            </p>
          )}
          <p className="mt-1.5 text-[10px] text-stone-500">
            La IA continuará desde aquí. Si pones el cursor en otra línea, el contexto se actualizará.
          </p>
        </div>
      )}

      {/* Character chips for sequence/dialogue */}
      {(mode === "sequence" || mode === "dialogue") && charNames.length > 0 && (
        <div className="mb-3" data-testid="sp-ai-charpicker">
          <p className="mb-1 text-[10px] uppercase tracking-widest text-stone-500">
            Personajes en la escena (opcional)
          </p>
          <div className="flex flex-wrap gap-1">
            {charNames.map((name) => (
              <button
                key={name}
                onClick={() => toggleChar(name)}
                data-testid={`sp-ai-charchip-${name}`}
                className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider transition ${
                  pickedChars.includes(name)
                    ? "bg-[#26775E] text-white"
                    : "border border-stone-300 bg-white text-stone-700 hover:border-[#26775E]"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sequence mode */}
      {mode === "sequence" && (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500">
            {useCursorContext && hasCtx ? "Intención (1 frase basta)" : "Instrucción"}
          </label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder={useCursorContext && hasCtx
              ? "Ej.: Ve una sombra fuera y decide salir."
              : "Ej.: Maturk y Agmet se encuentran en la playa. Ella le pide que se vaya."}
            rows={3}
            data-testid="sp-ai-instructions"
            className="mt-1 w-full resize-y rounded border border-stone-300 bg-white px-2 py-1 text-sm outline-none focus:border-[#26775E]"
          />

          {/* Suggest directions */}
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-2.5" data-testid="sp-ai-directions-block">
            <p className="mb-1.5 text-[11px] text-amber-900">
              <strong>¿Estás en blanco?</strong> Pide 3 caminos para la siguiente beat.
            </p>
            <input
              value={directionsHint}
              onChange={(e) => setDirectionsHint(e.target.value)}
              placeholder="Pista opcional: 'sube la tensión', 'meter un giro'…"
              data-testid="sp-ai-directions-hint"
              className="mb-2 w-full rounded border border-amber-300 bg-white px-2 py-1 text-xs outline-none focus:border-amber-600"
            />
            <button
              onClick={() => onSuggestDirections(directionsHint)}
              disabled={aiBusy}
              data-testid="sp-ai-suggest-directions-btn"
              className="rounded-full bg-amber-600 px-3 py-1 text-[10px] uppercase tracking-widest text-white hover:bg-amber-700 disabled:opacity-50"
            >
              ✦ Sugerir 3 caminos
            </button>
          </div>

          <p className="mt-2 text-[10px] text-stone-500">
            Devuelve una secuencia completa (cabecera, acción y diálogos).
          </p>
        </div>
      )}

      {/* Dialogue mode */}
      {mode === "dialogue" && (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500">Contexto de la escena</label>
          <textarea
            value={dlgContext}
            onChange={(e) => setDlgContext(e.target.value)}
            placeholder="Ej.: En la cocina, después de la pelea. Es la primera vez que hablan en horas."
            rows={3}
            data-testid="sp-ai-dlg-context"
            className="mt-1 w-full resize-y rounded border border-stone-300 bg-white px-2 py-1 text-sm outline-none focus:border-[#26775E]"
          />
          <label className="mt-2 text-[10px] uppercase tracking-widest text-stone-500">Indicaciones extra (opcional)</label>
          <textarea
            value={dlgExtra}
            onChange={(e) => setDlgExtra(e.target.value)}
            placeholder="Tono, ritmo, subtexto..."
            rows={2}
            data-testid="sp-ai-dlg-extra"
            className="mt-1 w-full resize-y rounded border border-stone-300 bg-white px-2 py-1 text-sm outline-none focus:border-[#26775E]"
          />
        </div>
      )}

      {/* Rewrite mode */}
      {mode === "rewrite" && (
        <div>
          <p className="mb-2 rounded border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700" data-testid="sp-ai-rewrite-help">
            {selectedCount > 0 ? (
              <>Reescribiré las <strong>{selectedCount}</strong> líneas seleccionadas según tus instrucciones.</>
            ) : (
              <>Selecciona primero líneas en el editor (con la casilla a la izquierda de cada línea) y luego escribe la instrucción aquí.</>
            )}
          </p>
          <label className="text-[10px] uppercase tracking-widest text-stone-500">Instrucción</label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Ej.: Hazlo más conciso. Más tenso. Cambia el subtexto."
            rows={3}
            data-testid="sp-ai-rewrite-instructions"
            className="mt-1 w-full resize-y rounded border border-stone-300 bg-white px-2 py-1 text-sm outline-none focus:border-[#26775E]"
          />
        </div>
      )}

      {/* Submit */}
      <div className="mt-3 flex justify-end">
        <button
          onClick={submit}
          disabled={aiBusy || (mode === "rewrite" && selectedCount === 0)}
          data-testid="sp-ai-submit"
          className="rounded-full bg-[#26775E] px-4 py-1.5 text-[11px] uppercase tracking-widest text-white transition hover:bg-stone-900 disabled:opacity-50"
        >
          {aiBusy ? "Generando…" : "✦ Generar"}
        </button>
      </div>

      {/* Banner "Generando…" con timer + cancelar (visible solo mientras la IA trabaja) */}
      {aiBusy && (
        <div
          className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-[#26775E]/40 bg-[#26775E]/5 px-3 py-2 text-xs text-stone-700"
          data-testid="sp-ai-progress"
        >
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#26775E] border-t-transparent" />
            <span>
              Generando<span className="inline-block min-w-[2.5em] text-stone-500"> · {formatElapsed(aiElapsed)}</span>
            </span>
            <span className="text-stone-400" title="La IA puede tardar lo que necesite — el polling continúa hasta que termina.">
              · sin límite
            </span>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              data-testid="sp-ai-cancel"
              className="rounded-md border border-stone-300 bg-white px-2 py-1 text-[11px] font-medium text-stone-700 transition hover:border-red-400 hover:text-red-600"
            >
              ✕ Cancelar
            </button>
          )}
        </div>
      )}

      {aiError && (
        <div className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-800" data-testid="sp-ai-error">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 text-base leading-none">⚠️</span>
            <div className="flex-1">{aiError}</div>
          </div>
          {canRetry && onRetry && (
            <button
              onClick={onRetry}
              disabled={aiBusy}
              data-testid="sp-ai-retry"
              className="mt-2 inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2 py-1 text-[11px] font-medium text-red-700 transition hover:border-red-400 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              ↻ Reintentar
            </button>
          )}
        </div>
      )}

      {/* Directions cards */}
      {directions && directions.directions && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="sp-ai-directions-preview">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-amber-800">Caminos sugeridos</span>
            <button
              onClick={onDismissDirections}
              data-testid="sp-ai-directions-dismiss"
              className="text-xs text-stone-500 hover:text-red-600"
            >
              Descartar
            </button>
          </div>
          {directions.directions.length === 0 && (
            <p className="text-center text-xs text-stone-500">La IA no pudo sugerir caminos.</p>
          )}
          <ul className="space-y-2">
            {directions.directions.map((d, i) => (
              <li key={i}>
                <button
                  onClick={() => pickDirection(d.instruction)}
                  data-testid={`sp-ai-direction-${i}`}
                  className="w-full rounded-lg border border-amber-300 bg-white p-3 text-left transition hover:border-amber-600 hover:shadow-sm"
                >
                  <p className="mb-1 font-serif text-sm font-bold text-stone-900">
                    {DIRECTION_EMOJIS[i] || "✦"} {d.label}
                  </p>
                  <p className="text-[11px] leading-relaxed text-stone-700">{d.description}</p>
                  <p className="mt-1.5 text-[10px] uppercase tracking-widest text-amber-700">→ Pulsa para usar como prompt</p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Preview */}
      {aiPreview && (
        <div className="mt-4 rounded-lg border border-[#26775E]/40 bg-[#26775E]/5 p-3" data-testid="sp-ai-preview">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-[#26775E]">Propuesta de la IA</span>
            <button
              onClick={onDismissPreview}
              data-testid="sp-ai-preview-dismiss"
              className="text-xs text-stone-500 hover:text-red-600"
            >
              Descartar
            </button>
          </div>
          {aiContext && (
            <p className="mb-2 text-[10px] italic text-stone-500">&ldquo;{aiContext.slice(0, 120)}{aiContext.length > 120 ? "…" : ""}&rdquo;</p>
          )}
          <div className="max-h-72 overflow-y-auto rounded border border-stone-200 bg-white p-2 font-mono text-[11px] leading-5">
            {(aiPreview.elements || []).map((el, i) => (
              <PreviewLine key={i} type={el.type} text={el.text} />
            ))}
            {(aiPreview.elements || []).length === 0 && (
              <p className="text-center text-stone-400">La IA no devolvió contenido</p>
            )}
          </div>
          {(aiPreview.elements || []).length > 0 && (
            <div className="mt-2 flex flex-wrap justify-end gap-2">
              <button
                onClick={() => onApplyPreview("end")}
                data-testid="sp-ai-apply-end"
                className="rounded-full bg-[#26775E] px-3 py-1 text-[11px] uppercase tracking-widest text-white hover:bg-stone-900"
              >
                {aiPreview.kind === "rewrite" ? "Reemplazar selección" : "Añadir al final"}
              </button>
              {aiPreview.kind !== "rewrite" && (
                <button
                  onClick={() => onApplyPreview("after-focus")}
                  data-testid="sp-ai-apply-cursor"
                  className="rounded-full border border-[#26775E] px-3 py-1 text-[11px] uppercase tracking-widest text-[#26775E] hover:bg-[#26775E]/10"
                >
                  Insertar tras el cursor
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
